// LIVE — Performance › Sales, computed from the whole Stripe account.
//
// salesStripe.js reads the account (every subscription, invoice, charge,
// refund and dispute, through production's read-only lookup); this file turns
// those records into the page's numbers. It is pure: no clock but the `now` it
// is handed, no network but the `load` salesSnapshot is handed.
//
// THE DEFINITIONS ARE STRIPE BILLING'S (docs.stripe.com/billing/subscriptions/
// analytics), with its "active when the first payment is received" setting —
// Stripe's other setting starts counting a subscription the moment its trial
// ends, paid or not, and on this account nearly every trial ends on a card
// that never pays, so it would report revenue nobody has collected.
//
//   MRR          the monthly-normalized price of every active or past_due
//                subscription (a yearly plan counts a twelfth; tax, metered
//                usage and 'once' coupons excluded, recurring discounts
//                taken off — production works that out per subscription),
//                counted from the subscription's first paid invoice. It
//                stops counting when the subscription is canceled — at the
//                cancel request, even one that runs to the period's end — or
//                stops being collected (Stripe's 'unpaid', or collection
//                paused). Trials never count. One-time payments are revenue,
//                never MRR.
//   subscriber   a customer with at least one subscription counting in MRR;
//                several subscriptions are still one subscriber. New = their
//                first time (not a comeback); churned = their last counting
//                subscription stopped.
//   churn rate   churned ÷ (subscribers when the range opened + new in it).
//   trial conversion rate   trials that paid ÷ trials that ended, in the range.
//   revenue      money collected (every charge captured, whatever sold it),
//                less refunds and disputes, each on the day it happened.
//                Stripe's fees are not taken off; they are reported beside it.
//   special deal a payment that is not a subscription invoice: a sale by
//                payment link (named after the link), a one-off invoice, or a
//                direct charge. Most of this account's revenue.
//   payouts      transfers to the bank, counted on the day they land; what
//                Stripe still holds is its balance, available plus pending.
//
// Ranges are local calendar days ending today, like every page in the app.
import { dayKeyOf, dayKeys, monthKeys, shortDay, shortMonth, deltaOf, formatMoney } from './sampleData.js'
import { fetchSalesAccount } from './salesStripe.js'

// ---- range ------------------------------------------------------------------

export const SALES_RANGE_KEYS = ['7d', '30d', '90d', '12m']
export const SALES_RANGE_LABELS = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '12m': 'Last 12 months',
}
export const DEFAULT_SALES_RANGE = '30d'
// '12m' is 52 whole weeks (364 days), not 365. The collections chart draws it
// as one bar per week, and 52 bars that tile the window exactly beat 52 bars
// plus a one-day stub at the front.
const RANGE_DAYS = { '7d': 7, '30d': 30, '90d': 90, '12m': 364 }

export function isSalesRange(key) {
  return SALES_RANGE_KEYS.includes(key)
}

// The calendar window a range key means, ending now. `start` is local midnight
// on the first day; `priorStart` is the same distance again before it, so the
// prior window every delta compares against is exactly as long as the range.
// An unknown key is the default, never a guess at what was meant.
export function rangeWindow(key, now) {
  const k = isSalesRange(key) ? key : DEFAULT_SALES_RANGE
  const days = RANGE_DAYS[k]
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const start = new Date(today)
  start.setDate(today.getDate() - (days - 1))
  const priorStart = new Date(start)
  priorStart.setDate(start.getDate() - days)
  return { key: k, label: SALES_RANGE_LABELS[k], days, start, end: new Date(now), priorStart }
}

// ---- statuses -----------------------------------------------------------------

// Stripe's subscription statuses, plus 'paused': an active subscription whose
// payment collection is paused, which bills nobody and so is shown apart.
// Ordered the way the page lists them — what is live first.
export const SUBSCRIPTION_STATUSES = ['active', 'past_due', 'trialing', 'paused', 'unpaid', 'incomplete', 'incomplete_expired', 'canceled']
// A charge: paid, failed, pending, or paid and then (partly) refunded.
export const PAYMENT_STATUSES = ['paid', 'failed', 'refunded', 'pending']

const LIVE = new Set(['active', 'past_due'])
const SOURCE_FALLBACK = {
  subscription: 'Subscription invoice',
  payment_link: 'Payment link',
  checkout: 'Checkout',
  invoice: 'Invoice',
  direct: 'Direct charge',
}
export const PAYOUT_STATUSES = ['paid', 'in_transit', 'pending', 'failed', 'canceled']
const NEVER_STARTED = new Set(['incomplete', 'incomplete_expired'])
const DAY_MS = 24 * 3600 * 1000

const msOf = (seconds) => {
  const n = Number(seconds)
  return Number.isFinite(n) && n > 0 ? n * 1000 : null
}
const iso = (t) => (t == null ? null : new Date(t).toISOString())
const at = (isoString) => (isoString ? Date.parse(isoString) : null)
const sum = (rows, key) => rows.reduce((total, r) => total + (r[key] || 0), 0)
const formatCents = (cents) => formatMoney(cents, { cents: true })

// A customer as a row shows it. Most of this account's customers have no name
// in Stripe, only an email, but their cards carry the cardholder's — so the
// customer's own name, else the name on their latest charge, else the email;
// and the email is a second line only when it says something the name did not.
// The same customer reads the same in every table.
function customerOf(customers, id, fallback = {}) {
  const c = (id && customers[id]) || {}
  const name = c.name || c.billingName || fallback.name || ''
  const email = c.email || fallback.email || ''
  return {
    id: id || null,
    name: name || email || 'Unknown customer',
    email: name ? email : '',
    dashboardUrl: c.dashboard_url || null,
  }
}

// ---- the model ----------------------------------------------------------------

/**
 * Stripe's answer, reduced to rows the page can count and show. Every amount
 * stays in the smallest currency unit, every instant becomes an ISO string.
 * Rows in a currency other than the account's main one are left out — sums
 * across currencies mean nothing — and a note says how many.
 */
export function buildSalesModel(raw) {
  if (!raw || raw.view !== 'sales' || !Array.isArray(raw.subscriptions)) {
    throw new TypeError('The answer was not a Stripe sales read.')
  }
  const counts = raw.currencies || {}
  const currency = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || 'usd'
  const mine = (row) => (row.currency || 'usd') === currency
  const customers = {}
  for (const [id, c] of Object.entries(raw.customers || {})) customers[id] = { ...c }
  const latestCharges = [...(raw.payments || [])].sort((a, b) => (a.created || 0) - (b.created || 0))
  for (const p of latestCharges) {
    if (p.customer_id && p.billing_name && customers[p.customer_id]) customers[p.customer_id].billingName = p.billing_name
  }

  // When each subscription first PAID: its earliest paid invoice that took
  // money (a trial's $0 invoice is "paid" too, and is not a payment).
  const firstPaid = new Map()
  for (const inv of raw.invoices || []) {
    const paidAt = msOf(inv.paid_at)
    if (inv.status !== 'paid' || !(inv.amount_paid > 0) || !inv.subscription_id || !paidAt) continue
    if (!firstPaid.has(inv.subscription_id) || paidAt < firstPaid.get(inv.subscription_id)) firstPaid.set(inv.subscription_id, paidAt)
  }
  const periodsBySub = new Map()
  for (const p of raw.periods || []) {
    const list = periodsBySub.get(p.subscription_id) || []
    list.push({ start: msOf(p.start), end: msOf(p.end), mrrCents: p.mrr, paid: p.status === 'paid' })
    periodsBySub.set(p.subscription_id, list)
  }

  const subscriptions = raw.subscriptions.filter(mine).map((s) => {
    const paused = s.status === 'active' && Boolean(s.paused)
    const status = paused ? 'paused' : s.status
    const periods = (periodsBySub.get(s.id) || []).sort((a, b) => a.start - b.start)
    const firstPaidAt = firstPaid.get(s.id) || null
    const canceledAt = msOf(s.canceled_at) || msOf(s.ended_at)
    // No longer collected: it stopped counting when the last period it paid
    // for ran out.
    const uncollected = paused || s.status === 'unpaid'
    const lastPaidEnd = periods.filter((p) => p.paid && p.mrrCents > 0).reduce((m, p) => Math.max(m, p.end), 0) || null
    let counting = { from: null, until: null }
    if (firstPaidAt && s.mrr > 0 && !NEVER_STARTED.has(s.status)) {
      const until = Math.min(canceledAt ?? Infinity, msOf(s.cancel_at) ?? Infinity, uncollected ? lastPaidEnd ?? firstPaidAt : Infinity)
      if (until > firstPaidAt) counting = { from: firstPaidAt, until: until === Infinity ? null : until }
    }
    const countsNow = counting.from != null && counting.until == null && LIVE.has(s.status)
    const trialStart = msOf(s.trial_start)
    const trialEnd = msOf(s.trial_end)
    let renewsAt = null
    if (s.status === 'trialing') renewsAt = trialEnd
    else if (LIVE.has(s.status) && !paused && !canceledAt) renewsAt = msOf(s.current_period_end)
    return {
      id: s.id,
      customer: customerOf(customers, s.customer_id),
      planName: s.product || 'Subscription',
      deal: s.deal || '',
      seats: s.quantity,
      mrrCents: s.mrr,
      amountCents: s.amount,
      interval: s.interval,
      intervalCount: s.interval_count,
      status,
      method: s.method || '',
      discount: s.discount || '',
      startedAt: iso(msOf(s.started)),
      trialStartedAt: iso(trialStart),
      // A trial canceled before it ran out ended when it was canceled.
      trialEndedAt: trialEnd ? iso(Math.min(trialEnd, canceledAt ?? Infinity)) : null,
      trialEndsAt: iso(trialEnd),
      firstPaidAt: iso(firstPaidAt),
      convertedAt: trialStart && firstPaidAt && firstPaidAt >= trialStart ? iso(firstPaidAt) : null,
      renewsAt: iso(renewsAt),
      canceledAt: iso(canceledAt),
      cancelAtPeriodEnd: Boolean(s.cancel_at_period_end),
      countsNow,
      // Live in Stripe's eyes, but its first invoice has never been paid: on
      // this account, a trial that ended on a card that declines.
      awaitingFirstPayment: LIVE.has(s.status) && !paused && !firstPaidAt && s.mrr > 0 && !canceledAt,
      counting,
      periods,
      dashboardUrl: s.dashboard_url || null,
    }
  })
  const order = (s) => SUBSCRIPTION_STATUSES.indexOf(s.status)
  subscriptions.sort((a, b) => order(a) - order(b) || at(b.startedAt) - at(a.startedAt))

  const payments = (raw.payments || [])
    .filter(mine)
    .map((p) => {
      const collected = p.status === 'succeeded' && p.captured !== false
      let status = 'pending'
      if (collected) status = p.amount_refunded > 0 ? 'refunded' : 'paid'
      else if (p.status === 'failed') status = 'failed'
      const source = p.source || (p.subscription_id ? 'subscription' : p.invoice_id ? 'invoice' : 'direct')
      return {
        id: p.id,
        customer: customerOf(customers, p.customer_id, { name: p.billing_name, email: p.billing_email }),
        description: p.description || p.deal || SOURCE_FALLBACK[source] || 'Payment',
        amountCents: p.amount,
        collectedCents: collected ? p.amount_captured : 0,
        refundedCents: p.amount_refunded || 0,
        feeCents: collected ? p.fee || 0 : 0,
        netCents: collected && p.net != null ? p.net : null,
        status,
        source,
        deal: p.deal || '',
        special: source !== 'subscription',
        method: p.method || '',
        failureMessage: p.failure_message || '',
        subscriptionId: p.subscription_id || null,
        invoiceId: p.invoice_id || null,
        createdAt: iso(msOf(p.created)),
        dashboardUrl: p.dashboard_url || null,
      }
    })
    .sort((a, b) => at(b.createdAt) - at(a.createdAt))

  // Refunds that went (or are going) through; a failed or canceled one moved
  // no money.
  const refunds = (raw.refunds || [])
    .filter((r) => mine(r) && (r.status === 'succeeded' || r.status === 'pending'))
    .map((r) => ({ id: r.id, chargeId: r.charge_id, amountCents: r.amount, createdAt: iso(msOf(r.created)), status: r.status }))
  const disputes = (raw.disputes || [])
    .filter(mine)
    .map((d) => ({ id: d.id, chargeId: d.charge_id, amountCents: d.amount, createdAt: iso(msOf(d.created)), status: d.status }))
  const openInvoices = (raw.invoices || [])
    .filter((i) => i.status === 'open' && mine(i))
    .map((i) => ({
      id: i.id,
      number: i.number || '',
      customer: customerOf(customers, i.customer_id),
      subscriptionId: i.subscription_id || null,
      amountRemainingCents: i.amount_remaining,
      attemptCount: i.attempt_count || 0,
      nextAttemptAt: iso(msOf(i.next_payment_attempt)),
      dueAt: iso(msOf(i.due_date)),
      createdAt: iso(msOf(i.created)),
      dashboardUrl: i.dashboard_url || null,
    }))

  // Transfers to the bank, newest first. A failed or canceled payout moved
  // nothing, and shows so.
  const payouts = (raw.payouts || [])
    .filter(mine)
    .map((p) => ({
      id: p.id,
      amountCents: p.amount,
      status: PAYOUT_STATUSES.includes(p.status) ? p.status : 'pending',
      createdAt: iso(msOf(p.created)),
      arrivesAt: iso(msOf(p.arrival_date) || msOf(p.created)),
      destination: p.destination || '',
      automatic: Boolean(p.automatic),
      description: p.description || '',
      failureMessage: p.failure_message || '',
      dashboardUrl: p.dashboard_url || null,
    }))
    .sort((a, b) => at(b.arrivesAt) - at(a.arrivesAt))
  const balance = {
    availableCents: ((raw.balance || {}).available || {})[currency] || 0,
    pendingCents: ((raw.balance || {}).pending || {})[currency] || 0,
  }
  // Stripe's fees that sit on no charge: Billing's, a dispute's. Stripe
  // books them as negative amounts.
  const fees = (raw.fees || [])
    .filter(mine)
    .map((f) => ({ id: f.id, amountCents: -f.amount, createdAt: iso(msOf(f.created)), description: f.description || '' }))

  const notes = [...(raw.notes || [])]
  const other = Object.entries(counts).filter(([c]) => c !== currency)
  if (other.length) {
    const rows = other.reduce((n, [, k]) => n + k, 0)
    notes.push(`${rows.toLocaleString()} Stripe ${rows === 1 ? 'record' : 'records'} in ${other.map(([c]) => c.toUpperCase()).join(', ')} left out; every figure here is ${currency.toUpperCase()}.`)
  }
  return {
    asOf: iso(msOf(raw.as_of)),
    currency: currency.toUpperCase(),
    subscriptions,
    payments,
    refunds,
    disputes,
    openInvoices,
    payouts,
    balance,
    fees,
    notes,
  }
}

// ---- MRR over time --------------------------------------------------------------

/**
 * MRR at instant `t`: every subscription counting then, at the rate the
 * invoice covering `t` billed it (its price then, not now), or its current
 * rate when no invoice covers `t`.
 */
export function mrrAt(subscriptions, t) {
  let total = 0
  for (const s of subscriptions) {
    const { from, until } = s.counting
    if (from == null || t < from || (until != null && t >= until)) continue
    const period = s.periods.find((p) => p.start <= t && t < p.end)
    total += period ? period.mrrCents : s.mrrCents
  }
  return total
}

// Each customer's spells as a subscriber: their subscriptions' counting spans,
// merged where they overlap or touch. The first spell's start is when they
// became a subscriber; every spell's end is a churn.
function subscriberSpells(subscriptions) {
  const byCustomer = new Map()
  for (const s of subscriptions) {
    if (s.counting.from == null) continue
    const key = s.customer.id || s.id
    const list = byCustomer.get(key) || []
    list.push([s.counting.from, s.counting.until ?? Infinity])
    byCustomer.set(key, list)
  }
  const spells = []
  for (const list of byCustomer.values()) {
    list.sort((a, b) => a[0] - b[0])
    const merged = []
    for (const [from, until] of list) {
      const last = merged[merged.length - 1]
      if (last && from <= last[1]) last[1] = Math.max(last[1], until)
      else merged.push([from, until])
    }
    spells.push(merged)
  }
  return spells
}

// The instant a 'YYYY-MM' month ends (local), one millisecond before the next
// one starts.
function monthEnd(key) {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m, 1).getTime() - 1
}

// ---- summaries ----------------------------------------------------------------

// The payments the page's table shows: inside the window, newest first. The
// model is already sorted that way, so this is a filter.
export function paymentsInRange(payments, win) {
  const from = win.start.getTime()
  const to = win.end.getTime()
  return payments.filter((p) => {
    const t = at(p.createdAt)
    return t >= from && t <= to
  })
}

// The payouts that landed (or are due to land) inside the window.
export function payoutsInRange(payouts, win) {
  const from = win.start.getTime()
  const to = win.end.getTime()
  return payouts.filter((p) => {
    const t = at(p.arrivesAt)
    return t >= from && t <= to
  })
}

/**
 * The tile numbers for `range`. MRR, ARR, subscribers, ARPU, past-due invoices
 * and renewals are as of `now` and ignore the range; everything else counts
 * the window and its equal-length predecessor, so a tile's delta was actually
 * computed.
 */
export function summarizeSales(model, range, now) {
  const win = rangeWindow(range, now)
  const nowMs = now.getTime()
  const t0 = win.start.getTime()
  const p0 = win.priorStart.getTime()
  const inWin = (t) => t != null && t >= t0 && t <= nowMs
  const inPrior = (t) => t != null && t >= p0 && t < t0
  const subs = model.subscriptions

  const counting = subs.filter((s) => s.countsNow)
  const mrrCents = sum(counting, 'mrrCents')
  const pastDueCounting = counting.filter((s) => s.status === 'past_due')
  const awaiting = subs.filter((s) => s.awaitingFirstPayment)
  const trialing = subs.filter((s) => s.status === 'trialing')
  const activeSubscribers = new Set(counting.map((s) => s.customer.id || s.id)).size
  const thisMonth = monthKeys(1, now)[0]
  const [y, m] = thisMonth.split('-').map(Number)
  const priorMonthMrrCents = mrrAt(subs, new Date(y, m - 1, 1).getTime() - 1)

  let newSubscribers = 0
  let priorNewSubscribers = 0
  let churned = 0
  let priorChurned = 0
  let subscribersAtRangeStart = 0
  let subscribersAtPriorStart = 0
  for (const spells of subscriberSpells(subs)) {
    if (inWin(spells[0][0])) newSubscribers++
    if (inPrior(spells[0][0])) priorNewSubscribers++
    for (const [from, until] of spells) {
      if (until <= nowMs && inWin(until)) churned++
      if (inPrior(until)) priorChurned++
      if (from < t0 && t0 < until) subscribersAtRangeStart++
      if (from < p0 && p0 < until) subscribersAtPriorStart++
    }
  }
  const churnBase = subscribersAtRangeStart + newSubscribers
  const priorChurnBase = subscribersAtPriorStart + priorNewSubscribers

  const pays = model.payments
  const collected = (p) => p.collectedCents > 0
  const paidIn = pays.filter((p) => collected(p) && inWin(at(p.createdAt)))
  const paidPrior = pays.filter((p) => collected(p) && inPrior(at(p.createdAt)))
  const failedIn = pays.filter((p) => p.status === 'failed' && inWin(at(p.createdAt)))
  const failedPrior = pays.filter((p) => p.status === 'failed' && inPrior(at(p.createdAt)))
  const refundsIn = model.refunds.filter((r) => inWin(at(r.createdAt)))
  const refundsPrior = model.refunds.filter((r) => inPrior(at(r.createdAt)))
  const specialIn = paidIn.filter((p) => p.special)
  const specialPrior = paidPrior.filter((p) => p.special)
  const linkIn = specialIn.filter((p) => p.source === 'payment_link')
  // Stripe's fees: on what was collected, less any it gave back on a refund,
  // plus the ones it books on their own (Billing's, a dispute's).
  const otherFeesIn = model.fees.filter((f) => inWin(at(f.createdAt)))
  const otherFeesPrior = model.fees.filter((f) => inPrior(at(f.createdAt)))
  const feesIn = sum(paidIn, 'feeCents') + sum(refundsIn, 'feeCents') + sum(otherFeesIn, 'amountCents')
  const feesPrior = sum(paidPrior, 'feeCents') + sum(refundsPrior, 'feeCents') + sum(otherFeesPrior, 'amountCents')

  // Payouts count when they land. Those on the way are any age: money that
  // has left Stripe and not yet reached the bank.
  const landed = (p) => p.status === 'paid'
  const payoutsIn = model.payouts.filter((p) => landed(p) && inWin(at(p.arrivesAt)))
  const payoutsPrior = model.payouts.filter((p) => landed(p) && inPrior(at(p.arrivesAt)))
  const onTheWay = model.payouts.filter((p) => p.status === 'in_transit' || p.status === 'pending')
  const payoutsFailed = model.payouts.filter((p) => p.status === 'failed' && inWin(at(p.createdAt)))
  const destinations = [...new Set(payoutsIn.map((p) => p.destination).filter(Boolean))]
  // A dispute takes its amount back the day it opens and returns it if won.
  const lost = (d) => d.status !== 'won'
  const disputesIn = model.disputes.filter((d) => lost(d) && inWin(at(d.createdAt)))
  const disputesPrior = model.disputes.filter((d) => lost(d) && inPrior(at(d.createdAt)))

  const trialsEnded = subs.filter((s) => s.trialEndedAt && at(s.trialEndedAt) <= nowMs && inWin(at(s.trialEndedAt)))
  const priorTrialsEnded = subs.filter((s) => s.trialEndedAt && inPrior(at(s.trialEndedAt)))
  const conversions = subs.filter((s) => inWin(at(s.convertedAt)))
  const priorConversions = subs.filter((s) => inPrior(at(s.convertedAt)))

  // Open invoices with a failed payment (or past their due date). The ones on
  // a subscription Stripe has since canceled are kept apart: Stripe stopped
  // trying to collect them when it canceled the subscription.
  const byId = new Map(subs.map((s) => [s.id, s]))
  const overdue = model.openInvoices.filter((i) => i.attemptCount > 0 || (i.dueAt && at(i.dueAt) < nowMs))
  const dead = (i) => {
    const s = i.subscriptionId && byId.get(i.subscriptionId)
    return Boolean(s) && (s.status === 'canceled' || s.status === 'incomplete_expired')
  }
  const pastDue = overdue.filter((i) => !dead(i))
  const abandoned = overdue.filter(dead)

  // Renewals are paying subscriptions' next invoices. A trial ending is not a
  // renewal, and neither is the next invoice of one that has never paid; the
  // trials are counted beside it.
  const in30 = nowMs + 30 * DAY_MS
  const due = (s) => s.renewsAt && at(s.renewsAt) > nowMs && at(s.renewsAt) <= in30
  const renewing = counting.filter((s) => due(s) && !s.cancelAtPeriodEnd)
  const trialsEnding = trialing.filter(due)

  return {
    window: {
      key: win.key,
      label: win.label,
      days: win.days,
      start: iso(t0),
      end: iso(nowMs),
      priorStart: iso(p0),
    },
    mrrCents,
    arrCents: mrrCents * 12,
    pastDueMrrCents: sum(pastDueCounting, 'mrrCents'),
    priorMonthMrrCents,
    mrrDeltaVsPriorMonth: deltaOf(mrrCents, priorMonthMrrCents, 'up'),
    activeSubscribers,
    activeSubscriptions: counting.length,
    pastDueSubscriptions: pastDueCounting.length,
    trialing: trialing.length,
    awaitingFirstPayment: { count: awaiting.length, mrrCents: sum(awaiting, 'mrrCents') },
    arpuCents: activeSubscribers ? Math.round(mrrCents / activeSubscribers) : 0,
    netRevenueCents: sum(paidIn, 'collectedCents') - sum(refundsIn, 'amountCents') - sum(disputesIn, 'amountCents'),
    priorNetRevenueCents: sum(paidPrior, 'collectedCents') - sum(refundsPrior, 'amountCents') - sum(disputesPrior, 'amountCents'),
    collectedCents: sum(paidIn, 'collectedCents'),
    specialDealsCents: sum(specialIn, 'collectedCents'),
    priorSpecialDealsCents: sum(specialPrior, 'collectedCents'),
    specialDeals: specialIn.length,
    paymentLinks: linkIn.length,
    paymentLinksCents: sum(linkIn, 'collectedCents'),
    subscriptionRevenueCents: sum(paidIn, 'collectedCents') - sum(specialIn, 'collectedCents'),
    priorSubscriptionRevenueCents: sum(paidPrior, 'collectedCents') - sum(specialPrior, 'collectedCents'),
    subscriptionPayments: paidIn.length - specialIn.length,
    feesCents: feesIn,
    priorFeesCents: feesPrior,
    payouts: {
      count: payoutsIn.length,
      paidCents: sum(payoutsIn, 'amountCents'),
      priorPaidCents: sum(payoutsPrior, 'amountCents'),
      onTheWay: onTheWay.length,
      onTheWayCents: sum(onTheWay, 'amountCents'),
      failed: payoutsFailed.length,
      destinations,
    },
    balance: {
      availableCents: model.balance.availableCents,
      pendingCents: model.balance.pendingCents,
      inStripeCents: model.balance.availableCents + model.balance.pendingCents,
    },
    disputes: disputesIn.length,
    disputesCents: sum(disputesIn, 'amountCents'),
    newSubscribers,
    priorNewSubscribers,
    churned,
    priorChurned,
    subscribersAtRangeStart,
    churnRate: churnBase ? churned / churnBase : null,
    priorChurnRate: priorChurnBase ? priorChurned / priorChurnBase : null,
    trialsStarted: subs.filter((s) => inWin(at(s.trialStartedAt))).length,
    trialsEnded: trialsEnded.length,
    trialConversions: conversions.length,
    priorTrialConversions: priorConversions.length,
    trialConversionRate: trialsEnded.length ? conversions.length / trialsEnded.length : null,
    priorTrialsEnded: priorTrialsEnded.length,
    paymentsSucceeded: paidIn.length,
    paymentsSucceededCents: sum(paidIn, 'collectedCents'),
    paymentsFailed: failedIn.length,
    paymentsFailedCents: sum(failedIn, 'amountCents'),
    priorPaymentsFailed: failedPrior.length,
    refunds: refundsIn.length,
    refundsCents: sum(refundsIn, 'amountCents'),
    priorRefunds: refundsPrior.length,
    priorRefundsCents: sum(refundsPrior, 'amountCents'),
    pastDueInvoices: pastDue.length,
    pastDueCents: sum(pastDue, 'amountRemainingCents'),
    abandonedInvoices: abandoned.length,
    abandonedCents: sum(abandoned, 'amountRemainingCents'),
    renewalsNext30: {
      count: renewing.length,
      dueCents: sum(renewing, 'amountCents'),
      trialsEnding: trialsEnding.length,
      trialsEndingCents: sum(trialsEnding, 'amountCents'),
    },
  }
}

/**
 * The two bar charts. `mrrByMonth` is month-end MRR for the last twelve months
 * whatever the range — the last bar is now, the MRR tile's figure;
 * `revenueByDay` is money collected per day of the range, or per week for
 * '12m', where 364 daily bars would be a texture, not a chart. Refunds are not
 * taken off the bars (the Net revenue tile does that), so a bar is exactly
 * "what was collected that day".
 */
export function salesSeries(model, range, now) {
  const win = rangeWindow(range, now)
  const year = now.getFullYear()
  const keys12 = monthKeys(12, now)
  const nowMrr = sum(model.subscriptions.filter((s) => s.countsNow), 'mrrCents')
  const mrrByMonth = keys12.map((key, i) => ({
    key,
    label: shortMonth(key, year),
    value: i === keys12.length - 1 ? nowMrr : mrrAt(model.subscriptions, monthEnd(key)),
  }))

  // Per day: what was collected, and how much of it was special deals — the
  // bar's tooltip says so, since one bar cannot be two colours.
  const byDay = new Map()
  for (const p of paymentsInRange(model.payments, win)) {
    if (!(p.collectedCents > 0)) continue
    const key = dayKeyOf(new Date(p.createdAt))
    const day = byDay.get(key) || { value: 0, special: 0 }
    day.value += p.collectedCents
    if (p.special) day.special += p.collectedCents
    byDay.set(key, day)
  }
  const bar = (key, label, days, tooltip) => {
    const value = days.reduce((total, k) => total + (byDay.get(k) || { value: 0 }).value, 0)
    const special = days.reduce((total, k) => total + (byDay.get(k) || { special: 0 }).special, 0)
    const out = { key, label, value }
    if (tooltip) out.tooltip = tooltip
    if (special > 0) out.sub = `${formatCents(special)} special deals`
    return out
  }
  const keys = dayKeys(win.days, now)
  const weekly = win.key === '12m'
  let revenueByDay
  if (weekly) {
    revenueByDay = []
    for (let i = 0; i < keys.length; i += 7) {
      const week = keys.slice(i, i + 7)
      revenueByDay.push(bar(week[0], shortDay(week[0]), week, `Week of ${shortDay(week[0])}`))
    }
  } else {
    revenueByDay = keys.map((key) => bar(key, shortDay(key), [key]))
  }
  return { mrrByMonth, revenueByDay, bucket: weekly ? 'week' : 'day' }
}

/**
 * Subscriptions by Stripe product, as of now: how many pay (count in MRR) and
 * the MRR they carry, beside the trials and the never-paid ones on the same
 * product. The price is per seat per month when every paying subscription on
 * the product pays the same; otherwise the product is custom-priced.
 */
export function salesPlans(model) {
  const groups = new Map()
  for (const s of model.subscriptions) {
    if (!(s.countsNow || s.status === 'trialing' || s.awaitingFirstPayment)) continue
    const g = groups.get(s.planName) || { key: s.planName, name: s.planName, active: 0, mrrCents: 0, trialing: 0, awaiting: 0, perSeat: new Set() }
    if (s.countsNow) {
      g.active++
      g.mrrCents += s.mrrCents
      g.perSeat.add(Math.round(s.mrrCents / Math.max(s.seats || 1, 1)))
    } else if (s.status === 'trialing') g.trialing++
    else g.awaiting++
    groups.set(s.planName, g)
  }
  return [...groups.values()]
    .sort((a, b) => b.mrrCents - a.mrrCents || b.active - a.active || b.trialing - a.trialing || a.name.localeCompare(b.name))
    .map(({ perSeat, ...g }) => ({ ...g, monthlyCents: perSeat.size === 1 ? [...perSeat][0] : null, custom: perSeat.size > 1 }))
}

// ---- the snapshot -----------------------------------------------------------

/**
 * What the Sales page reads. Resolves, never rejects: the page renders whatever
 * comes back, and a rejected promise there is a spinner that never ends.
 * `available` says whether there is anything to show; when there is not,
 * `reason` says why in the server's own words.
 *
 *   range     one of SALES_RANGE_KEYS; anything else is the default.
 *   now       the instant "today" is read from. Defaults to the real clock.
 *   load      where the Stripe records come from (tests hand in their own).
 */
export async function salesSnapshot({ range, now, load = fetchSalesAccount } = {}) {
  const key = isSalesRange(range) ? range : DEFAULT_SALES_RANGE
  let raw
  try {
    raw = await load()
  } catch (err) {
    return {
      available: false,
      mode: 'live',
      range: key,
      status: err && typeof err.status === 'number' ? err.status : null,
      reason: err && err.message ? err.message : 'Stripe could not be read.',
    }
  }
  try {
    const clock = now == null ? new Date() : now instanceof Date ? now : new Date(now)
    const model = buildSalesModel(raw)
    return {
      available: true,
      mode: 'live',
      asOf: model.asOf,
      currency: model.currency,
      range: key,
      summary: summarizeSales(model, key, clock),
      series: salesSeries(model, key, clock),
      plans: salesPlans(model),
      payments: paymentsInRange(model.payments, rangeWindow(key, clock)),
      payouts: payoutsInRange(model.payouts, rangeWindow(key, clock)),
      subscriptions: model.subscriptions,
      notes: model.notes,
    }
  } catch (err) {
    return {
      available: false,
      mode: 'live',
      range: key,
      status: null,
      reason: `Stripe's answer could not be read: ${err && err.message ? err.message : 'unknown error'}`,
    }
  }
}
